import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  base_colorSurfaceMutedHover,
  base_contentHover,
  charts_categorical4,
  cta_buttonSecondaryBackground,
  cta_buttonSecondaryBackgroundHover,
  cta_buttonSecondaryBorder,
  cta_buttonSecondaryText,
  cta_linkTextPrimary,
  cta_linkTextPrimaryHover,
  tag_colorTagBlueBackground,
  tag_colorTagBlueText,
  text_colorTextPrimary,
  text_colorTextSecondary,
  text_colorTextSuccess,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import { font_lg, font_md, font_sm } from 'ext-ui/style/typography'
import {
  chartPanel,
  chartsGrid,
  chartTitle,
} from 'src/charting/reportingCardLayout.style'
import { css } from 'styled-components'

export { chartPanel, chartsGrid, chartTitle }

// Gentle skeleton pulse (spec: 1.2s ease-in-out; disabled under reduced-motion).
// Inlined per skeleton as an @keyframes block referenced by name (see
// LoadingShimmer) — neither a `keyframes` interpolation nor a shared css block
// survives the `css` prop transform, so the rule is repeated literally below.
const SKELETON_PULSE = `
  animation: reporting-skeleton-pulse 1.2s ease-in-out infinite;

  @keyframes reporting-skeleton-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const page = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
`

export const subtitle = css`
  ${font_md};
  color: ${text_colorTextSecondary};
`

// Page-header controls (right side, on the title row): the shared date control
// next to the currency toggle (spec: "date control + currency toggle stretched
// right, same row"). No "Currency" text label, per the spec.
export const toolbar = css`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing(4)};
  flex-wrap: wrap;
`

export const dateRangeControl = css`
  display: inline-flex;

  [data-dropdown-button-trigger] {
    padding-inline: ${spacing(4)};
  }
`

export const panelHeader = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing(4)};

  @media (max-width: 640px) {
    flex-direction: column;
  }
`

export const panelHeaderTitle = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2)};
`

export const chartDescription = css`
  ${font_sm};
  color: ${text_colorTextSecondary};
  margin: ${spacing(1)} 0 0;
`

export const statsRow = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing(8)};
`

export const stat = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
`

export const statLabel = css`
  ${font_sm};
  color: ${text_colorTextSecondary};
`

export const statValue = css`
  ${font_lg};
  color: ${text_colorTextPrimary};
  font-variant-numeric: tabular-nums;
`

export const chartBody = css`
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: stretch;
  justify-content: center;
  color: ${text_colorTextSecondary};
  text-align: center;

  > div {
    width: 100%;
    height: 100%;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`

export const panelMessage = css`
  ${font_md};
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${text_colorTextSecondary};
  text-align: center;

  p {
    margin: 0;
  }
`

export const panelMessageDetail = css`
  ${font_sm};
  margin-top: ${spacing(1)};
  color: ${text_colorTextSecondary};
`

export const retryButton = css`
  ${font_sm};
  margin-top: ${spacing(3)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${cta_linkTextPrimary};
  cursor: pointer;
  text-decoration: underline;

  &:hover,
  &:focus-visible {
    color: ${cta_linkTextPrimaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${charts_categorical4};
    outline-offset: 2px;
  }
`

export const chartSkeleton = css`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  align-items: flex-end;
  gap: ${spacing(3)};
`

export const chartSkeletonBar = css`
  flex: 1;
  min-height: 44px;
  border-radius: ${spacing(1)} ${spacing(1)} 0 0;
  background: ${base_colorSurfaceMutedHover};
  ${SKELETON_PULSE}
`

export const topSpendersBody = css`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`

export const topSpendersList = css`
  margin: 0;
  padding: 0;
  list-style: none;
`

export const topSpenderRow = css`
  display: flex;
  height: 48px;
  align-items: center;
  gap: ${spacing(3)};
  padding: 0 ${spacing(2)};
  border-top: 1px solid ${base_colorContentBorder};
  color: ${text_colorTextPrimary};

  &:first-child {
    border-top: 0;
  }

  &:hover {
    background: ${base_contentHover};
  }
`

export const topSpenderRank = css`
  ${font_sm};
  width: 16px;
  flex: 0 0 16px;
  color: ${text_colorTextSecondary};
  font-variant-numeric: tabular-nums;
  text-align: center;
`

export const topSpenderAvatar = css`
  display: flex;
  width: ${spacing(8)};
  height: ${spacing(8)};
  flex: 0 0 ${spacing(8)};
`

export const topSpenderAvatarInitials = css`
  ${font_sm};
  color: ${text_colorTextSuccess};
  font-weight: 500;
  text-transform: uppercase;
`

export const topSpenderName = css`
  ${font_md};
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: ${text_colorTextPrimary};
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const topSpenderAmount = css`
  ${font_md};
  color: ${text_colorTextPrimary};
  font-variant-numeric: tabular-nums;
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
`

export const topSpenderSkeletonRow = css`
  display: flex;
  height: 48px;
  align-items: center;
  gap: ${spacing(3)};
  padding: 0 ${spacing(2)};
  border-top: 1px solid ${base_colorContentBorder};

  &:first-child {
    border-top: 0;
  }
`

export const topSpenderSkeletonAvatar = css`
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 50%;
  background: ${base_colorSurfaceMutedHover};
  ${SKELETON_PULSE}
`

export const topSpenderSkeletonName = css`
  width: 42%;
  height: 12px;
  border-radius: ${spacing(1)};
  background: ${base_colorSurfaceMutedHover};
  ${SKELETON_PULSE}
`

export const topSpenderSkeletonAmount = css`
  width: 72px;
  height: 12px;
  margin-left: auto;
  border-radius: ${spacing(1)};
  background: ${base_colorSurfaceMutedHover};
  ${SKELETON_PULSE}
`

// DS Button variant="secondary" for admin gated-state CTAs.
// Matches the secondary button spec: bordered, bg fill, h40, px32, radius8.
export const gatedButton = css`
  ${font_md};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: ${spacing(3)};
  height: ${spacing(10)};
  padding: 0 ${spacing(8)};
  border-radius: ${cornerRadius};
  border: 1px solid ${cta_buttonSecondaryBorder};
  background: ${cta_buttonSecondaryBackground};
  color: ${cta_buttonSecondaryText};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${cta_buttonSecondaryBackgroundHover};
  }

  &:focus-visible {
    outline: 2px solid ${charts_categorical4};
    outline-offset: 2px;
  }
`

// Inline lock icon + subcopy for members (no-permission) gated state.
// Matches panelMessageDetail type scale (font_sm / grey500).
export const memberSubcopy = css`
  ${font_sm};
  display: inline-flex;
  align-items: center;
  gap: ${spacing(1)};
  margin-top: ${spacing(1)};
  color: ${text_colorTextSecondary};
`

// DS Pill "Pro" shown right of the panel title when the category chart
// is gated by tier (not_entitled state).
export const proPill = css`
  ${font_sm};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${spacing(5)};
  padding: 0 ${spacing(1)};
  border-radius: ${spacing(1)};
  background: ${tag_colorTagBlueBackground};
  color: ${tag_colorTagBlueText};
  flex-shrink: 0;
`

// Wrapper for the not_entitled teaser-preview (frosted sample chart behind message).
export const gateTeaser = css`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: ${spacing(1)};
`

// Absolute aria-hidden sample chart behind the not_entitled overlay.
export const teaserChart = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  gap: ${spacing(3)};
  padding: ${spacing(4)};
  filter: blur(6px);
  opacity: 0.55;
  pointer-events: none;
`

// Frosted scrim + centering container over the teaser chart.
// Uses a radial gradient to keep the message legible while the blurred
// bars peek at the edges. frosted-overlay is a DS gap — no token yet.
export const teaserOverlay = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    ellipse 85% 75% at 50% 50%,
    color-mix(in srgb, ${base_colorRaisedSurface} 88%, transparent) 0%,
    color-mix(in srgb, ${base_colorRaisedSurface} 80%, transparent) 55%,
    color-mix(in srgb, ${base_colorRaisedSurface} 32%, transparent) 100%
  );
`
